import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import {
  CancelDrop,
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  Modifiers,
  useDroppable,
  useSensors,
  useSensor,
  MeasuringStrategy,
  KeyboardCoordinateGetter,
  defaultDropAnimationSideEffects,
  closestCorners,
} from '@dnd-kit/core';
import {
  AnimateLayoutChanges,
  SortableContext,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
  SortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { coordinateGetter as multipleContainersCoordinateGetter } from './multipleContainersKeyboardCoordinates.ts';

import { Item, Container, ContainerProps } from '../../components/index.ts';

import { createRange, createUuid, DEFAULT_TASK_TITLE } from '../../utilities/index.ts';
import { Arguments } from '@dnd-kit/sortable/dist/hooks/useSortable';
import { Collision } from '@dnd-kit/core/dist/utilities/algorithms/types';

import {
  Pencil2Icon,
  RocketIcon,
  CheckCircledIcon,
  PlusIcon,
  HamburgerMenuIcon,
  GearIcon,
  TrashIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';

import { DetailedPanel } from '../DetailedPanel/DetailedPanel';

// import type {Active, RectMap} from '@dnd-kit/core/dist/store';
// import type {ClientRect, Coordinates} from '@dnd-kit/core/dist/types';

import styles from './MultipleContainers.module.css';
// import { TaskItemCategory } from '../../types/types.ts';
import { useTaskItemStore } from '../../stores/useTaskItemStore.ts';
import {
  DAY_,
  List,
  ItemsMap,
  useTaskListStore,
  getListIds,
  PRIORITY,
  defaultSystemListMap,
  TRASH_LIST_ID,
} from '../../stores/useTaskListStore.ts';
import { useIsMobile } from '../../hooks/useIsMobile';
import { uuid } from '../../types/types.ts';
import { LoremIpsum } from 'lorem-ipsum';

// import { TaskItemMDXEditor } from '../TaskItemMDXEditor/TaskItemMDXEditor.tsx';
// import { TaskItemViewer } from '../TaskItemViewer/TaskItemViewer.tsx';
import { TaskItem } from '../TaskItem/TaskItem.tsx';
import {
  Badge,
  Box,
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Dialog,
  ScrollArea,
} from '@radix-ui/themes';
import * as cn from 'classnames';
import UserListCalendar from '../UserListCalendar/UserListCalendar';
import { format } from 'date-fns';
import { useDebounce } from '@uidotdev/usehooks';
import { consoleLog } from '../../utilities/console.ts';

// const getDayLabel = (list: List): React.ReactNode => {
//   const now = new Date();
//   // All list that can not find `DAY_` prefix in all keys
//   if (list.keys?.findIndex((v) => v.startsWith(DAY_)) == -1) {
//     return list.label || '';
//   } else {
//     // Day list
//     const createdDate = list.createdDate ? new Date(list.createdDate) : new Date();
//     const dateString = format(createdDate, 'MM/dd/yyyy');
//     // TODO: add a ' < Today > ' buttongroup.
//     // if (now.isSame(createdDate, 'day')) {
//     //   return 'Today' + ' - ' + dateString;
//     // } else if (now.subtract(1, 'day').isSame(createdDate, 'day')) {
//     //   return 'Yesterday' + ' - ' + dateString;
//     // } else if (now.add(1, 'day').isSame(createdDate, 'day')) {
//     //   return 'Tomorrow' + ' - ' + dateString;
//     // }
//     return dateString;
//   }
// };

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

/*show add new item button when there is already one task in the container*/
function createContainerFooter(
  onAddNewItemClick: (options?: Record<string, string | boolean>) => void
) {
  return (
    <div className={styles.addNewItemContainer}>
      <Button
        variant={'soft'}
        onClick={() => onAddNewItemClick()}
        className={styles.addNewItemButton}
      >
        <Pencil2Icon />
        <span className={styles.text}>New Task</span>
      </Button>

      <div className={styles.addNewItemDropdown}>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="soft" className={styles.addNewItemDropdownTrigger}>
              <DropdownMenu.TriggerIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            <DropdownMenu.Item
              // shortcut="⌘ E"
              onClick={() => onAddNewItemClick({ type: 'NEW_DONE' })}
            >
              {/* <RocketIcon /> */}
              <CheckCircledIcon />
              <span className={styles.text}>Add Completed</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}

function DroppableContainer(
  droppableContainerProps: ContainerProps & {
    disabled?: boolean;
    showDroppable?: boolean;
    id: uuid;
    items: uuid[];
    style?: React.CSSProperties;
    onAddNewItemClick?: (options?: Record<string, string | boolean>) => void;
  }
) {
  const {
    children,
    columns = 1,
    disabled,
    showDroppable,
    id,
    items,
    style,
    onAddNewItemClick,
    ...props
  } = droppableContainerProps;

  const {
    active,
    attributes,
    isDragging,
    listeners,
    over,
    setNodeRef,
    transition,
    transform,
  } = useSortable({
    id,
    data: {
      type: 'container',
      children: items,
    },
    animateLayoutChanges,
  } as Arguments);
  const isOverContainer = over
    ? (id === over.id && active?.data.current?.type !== 'container') ||
    items.includes(over.id?.toString())
    : false;

  const footer = null; // createContainerFooter(onAddNewItemClick)

  return (
    <Container
      id={id}
      ref={disabled ? undefined : setNodeRef}
      style={{
        ...style,
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
      }}
      hover={isOverContainer}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      // showActions={true}
      showDroppable={showDroppable}
      // onClick={onClearActiveItem}
      onAddNewItemClick={onAddNewItemClick}
      columns={columns}
      footer={footer}
      {...props}
    >
      {children}
    </Container>
  );
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

interface Props {
  adjustScale?: boolean;
  cancelDrop?: CancelDrop;
  columns?: number;
  containerStyle?: React.CSSProperties;
  coordinateGetter?: KeyboardCoordinateGetter;

  getItemStyles?(args: {
    value: uuid;
    index: number;
    overIndex: number;
    isDragging: boolean;
    containerId: uuid;
    isSorting: boolean;
    isDragOverlay: boolean;
  }): React.CSSProperties;

  wrapperStyle?(args: { index: number }): React.CSSProperties;

  // itemCount?: number;
  // items?: Items;
  handle?: boolean;
  renderItem?: any;
  strategy?: SortingStrategy;
  modifiers?: Modifiers;
  minimal?: boolean;
  trashable?: boolean;
  scrollable?: boolean;
  vertical?: boolean;
}

export const TRASH_ID = 'void';
/// PLACEHOLDER_ID is used for adding new items to the list, not used now
// const PLACEHOLDER_ID = 'placeholder';
// const empty: UniqueIdentifier[] = [];

export function MultipleContainers(props: Props) {
  const {
    adjustScale = false,
    cancelDrop,
    columns,
    handle = false,
    // itemCount = 3,
    // items: initialItems,
    containerStyle,
    // coordinateGetter = multipleContainersCoordinateGetter,
    getItemStyles = () => ({}),
    wrapperStyle = () => ({}),
    minimal = false,
    modifiers,
    renderItem,
    strategy = verticalListSortingStrategy,
    trashable = false,
    vertical = false,
    scrollable,
  } = props;

  // const [items, setItems] = useState<Items>(
  //   () =>
  //     initialItems ?? {
  //       PRIORITY_A: [],
  //       PRIORITY_B: createRange(itemCount, (index) => `B${index + 1}`),
  //       PRIORITY_C: createRange(itemCount, (index) => `C${index + 1}`),
  //       PRIORITY_D: createRange(itemCount, (index) => `D${index + 1}`),
  //       TODAY: [],
  //     },
  // );

  // Items is a dictionary, key as listId, value as an array of related task item ids.
  const items = useTaskListStore((s) => s.itemsMap);
  const sidebarListsContainerRef = useRef<HTMLDivElement>(null);
  // user list keys are muted, task list is immutable (could be updated by configuration only)
  const userListMap = useTaskListStore((s) => s.userListMap);
  const taskListMap = useTaskListStore((s) => s.taskListMap);
  const visibleUserListIds = useTaskListStore((s) => s.visibleUserListIds);
  // One container is a viewer instance of one List
  const userListContainers = getListIds(userListMap);
  const taskListContainers = getListIds(taskListMap);
  const listMap = useMemo(() => {
    const merged = { ...userListMap };
    for (const key of Object.keys(defaultSystemListMap)) {
      if (taskListMap[key]) {
        merged[key] = {
          ...taskListMap[key],
          iconMutable:
            defaultSystemListMap[key]?.iconMutable ??
            taskListMap[key]?.iconMutable,
        };
      } else {
        merged[key] = defaultSystemListMap[key];
      }
    }
    return merged;
  }, [userListMap, taskListMap]);
  const containers = getListIds(listMap);

  const defaultListIds = useMemo(() => {
    return [
      '223270cb-72e5-43be-959c-d4e1744a96a6', // Important
      '71c4cdf4-0ff3-4722-8069-47a2bb9dd3de', // Urgent
      'c3565f9e-50e4-4a96-966b-ba95ea2c3292', // Inbox
    ];
  }, []);

  const orderedListIds = useMemo(() => {
    const customListIds = Object.keys(userListMap).filter(
      (id) => !defaultListIds.includes(id)
    );
    return [...defaultListIds, ...customListIds];
  }, [userListMap, defaultListIds]);

  const addUserList = useTaskListStore((s) => s.addUserList);

  const handleCreateNewList = () => {
    const newListId = addUserList('Custom List', { custom: createUuid() });
    setJustCreatedListId(newListId);
  };

  const setItems = useTaskListStore((s) => s.setItemsMap);
  const updateItems = useTaskListStore((s) => s.updateItemsMap);
  const updateListDate = useTaskListStore((s) => s.updateListDate);
  const removeItemFromItemsMap = useTaskListStore(
    (s) => s.removeItemFromItemsMap
  );

  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleExportBackup = () => {
    try {
      const backupData = {
        userListMap: useTaskListStore.getState().userListMap,
        taskListMap: useTaskListStore.getState().taskListMap,
        itemsMap: useTaskListStore.getState().itemsMap,
        items: useTaskItemStore.getState().items,
      };
      const dataStr =
        'data:text/json;charset=utf-8,' +
        encodeURIComponent(JSON.stringify(backupData, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute('href', dataStr);
      downloadAnchor.setAttribute(
        'download',
        `donelit_backup_${new Date().toISOString().slice(0, 10)}.json`
      );
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    } catch (error) {
      console.error('Backup export failed', error);
      alert('Failed to export backup data.');
    }
  };

  const allTaskItems = useTaskItemStore((s) => s.items);
  const deletedItems = useMemo(() => {
    return Object.values(allTaskItems)
      .filter((item) => item.deleted)
      .sort((a, b) => {
        const timeA = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
        const timeB = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;
        return timeB - timeA;
      });
  }, [allTaskItems]);

  // Current active item id, this is the local state that managed by Item Store
  const activeItemId = useTaskItemStore((s) => s.activeItemId);
  const setActiveItemId = useTaskItemStore((s) => s.setActiveItemId);

  // Tracks the ID of a newly created list container to scroll it into view
  const [justCreatedListId, setJustCreatedListId] = useState<string | null>(null);

  // Scroll a newly created list container into view once it is mounted/registered
  // useEffect(() => {
  //   if (!justCreatedListId) return;
  //   const containerEl = containerRefsMap.current.get(justCreatedListId);
  //   if (containerEl) {
  //     containerEl.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'nearest',
  //     });
  //     setJustCreatedListId(null);
  //   }
  // }, [justCreatedListId]);
  const setNextActiveItemId = useTaskItemStore((s) => s.setNextActiveItemId);
  const updateItemContent = useTaskItemStore((s) => s.updateContent);
  const enableGlobalReadOnly = useTaskItemStore((s) => s.enableGlobalReadOnly);
  const disableGlobalReadOnly = useTaskItemStore(
    (s) => s.disableGlobalReadOnly
  );

  // When the droppable container is explicitly clicked, set the active item to none
  // const handleClickToClearActiveItem = (event: React.MouseEvent<HTMLElement>) => {
  //   // Stop propagation if the target is not the container itself
  //   if (event.target !== event.currentTarget) {
  //     event.stopPropagation();
  //     return;
  //   }
  //   setActiveItemId(null);
  // };

  /// TODO: we don't allow dragging/adding/removing containers, so we don't need this
  // const [containers, setContainers] = useState(
  //   Object.keys(items) as uuid[],
  // );
  // const containers = Object.keys(items) as uuid[];
  // consoleLog('containers', containers);

  // Current active item id, this is the local state that managed by DndContext
  // In original offcial MultipleContainers, container and item are both movable, so activeId could be the
  // id of the moving container: which is a big issue that needs to consider when we refactor.
  const [activeId, setActiveId] = useState<uuid | null>(null);
  const lastOverId = useRef<uuid | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  // const isSortingContainer = activeId ? containers.includes(activeId) : false;

  // Only one container is being sorted at a time. The motivation is to fix when two vertical virtual containers
  // overlap, dragging an item will cause indefinite loop
  const [sortingContainerId, setSortingContainerId] = useState<uuid | null>(
    null
  );
  // This container will highlight with an showDroppable indicator
  const [showDroppableContainerId, setShowDroppableContainerId] =
    useState<uuid | null>(null);

  // States and mouse handlers for resizable sidebar width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('donelit-sidebar-width');
    return saved ? parseInt(saved, 10) : 360;
  });

  const isMobile = useIsMobile();

  const handleSidebarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = Math.min(sidebarWidth, window.innerWidth * 0.5);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      // Keep desktop default min-width of 320px, and set dynamic max-width to 50vw (50% of viewport width)
      const newWidth = Math.max(
        320,
        Math.min(window.innerWidth * 0.5, startWidth + deltaX)
      );
      setSidebarWidth(newWidth);
      localStorage.setItem('donelit-sidebar-width', newWidth.toString());
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };

    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */

  /**
   * Custom collision optimized for done lit
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      // 1. Check if the pointer is intersecting with the detailed panel drop-zone (desktop only)
      const panelContainer = !isMobile
        ? args.droppableContainers.find(
          (c) => c.id === 'detailed-panel-drop-zone'
        )
        : undefined;
      if (panelContainer) {
        const panelIntersection = pointerWithin({
          ...args,
          droppableContainers: [panelContainer],
        });
        if (panelIntersection.length > 0) {
          return panelIntersection;
        }
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => container.id in items
        ),
      });

      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
          pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      // consoleLog(`overid`, overId, overId && overId in items ? `[is container]` : '');

      const getDrops = (args: any) => {
        // it's a container
        if (overId && overId in items) {
          // find items inside, so sorting inside the container is allowed
          if (overId == sortingContainerId) {
            return args.droppableContainers.filter((container: any) =>
              items[overId].includes(container.id)
            );
          }
          // moving to other containers, return the whole container.
          else {
            return [{ id: overId }] as Collision[];
          }
        }

        return args.droppableContainers;
      };

      const itemIntersection = closestCenter({
        ...args,
        droppableContainers: getDrops(args),
      });
      return itemIntersection;

      /// TODO: study if lastOverId makes sense
      /*
      lastOverId.current = overId?.toString() || null;
      
      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current
        ? ([{ id: lastOverId.current }] as Collision[])
        : [];
      */
    },
    [activeId, items, sortingContainerId]
  );

  /**
   * Cloned items are used to revert to the state if the drag is cancelled.
   */
  const [clonedItems, setClonedItems] = useState<ItemsMap | null>(null);

  const activationConstraint = {
    distance: 5,
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    // By passing empty keyboardCodes, we don't want keyboard to active the item
    useSensor(KeyboardSensor, {
      // coordinateGetter,
      keyboardCodes: {
        start: [],
        cancel: [],
        end: [],
      },
    })
  );
  const findContainer = (id: uuid) => {
    if (id in items) {
      return id.toString();
    }

    return Object.keys(items)
      .find((key) => items[key].includes(id.toString()))
      ?.toString();
  };

  const getIndex = (id: uuid) => {
    const container = findContainer(id);

    if (!container) {
      return -1;
    }

    const index = items[container].indexOf(id.toString());

    return index;
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  const getPriority = (containerId: uuid) => {
    const list = listMap?.[containerId];
    return list?.keys?.priority || '';
  };

  const renderDroppableContainer = (containerId: uuid) => {
    const list = listMap?.[containerId];
    const showDroppable =
      showDroppableContainerId === containerId &&
      sortingContainerId !== containerId;
    return (
      <div
        className={styles.droppableContainer}
        key={`dropContainer-${containerId}`}
      >
        <DroppableContainer
          key={containerId}
          id={containerId}
          label={minimal ? undefined : list?.label || ''}
          icon={list.icon || ''}
          showActions={true}
          iconMutable={list?.iconMutable || false}
          //todo-pudu: when there are more than 2 items, here is the best place to change columns to 2
          columns={columns}
          items={items[containerId]}
          scrollable={scrollable}
          // todo revert to params
          style={containerStyle}
          showDroppable={showDroppable}
          unstyled={minimal}
          // onRemove={() => handleRemove(containerId)}
          onAddNewItemClick={(options?: any) =>
            handleAddNewItem(containerId, options)
          }
        >
          <SortableContext
            items={items[containerId] || []}
            strategy={strategy}
            id={containerId}
          >
            {items[containerId] ? (
              items[containerId].map((value, index) => {
                const isItemDisabled =
                  sortingContainerId !== null &&
                  sortingContainerId !== containerId;
                return (
                  <SortableItem
                    disabled={isItemDisabled}
                    key={value}
                    id={value}
                    index={index}
                    // Only when the item is editor active
                    // we respect the handle props, otherwise we don't allow dragging

                    // TODO: even totally hide drag handle.
                    // TODO: add minimun drag distance, so click to edit is easier
                    // TODO: rename activeItemId to editorActiveItemId

                    // TODO: open a new door for floating menu, to keep the focus state?
                    // handle={activeItemId === value ? true : handle}
                    handle={handle}
                    style={getItemStyles}
                    wrapperStyle={wrapperStyle}
                    renderItem={renderItem}
                    onRemove={() => {
                      useTaskItemStore.getState().deleteItem(value);
                    }}
                    containerId={containerId}
                    useDragOverlay={false}
                    getIndex={getIndex}
                  />
                );
              })
            ) : (
              <div>No items</div>
            )}
          </SortableContext>
        </DroppableContainer>
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      autoScroll={false}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        consoleLog('[onDragStart]', active.id, active.data);

        // set the current sorting container
        const sortingContainerId = active.data?.current?.sortable?.containerId;
        setSortingContainerId(sortingContainerId);
        setShowDroppableContainerId(sortingContainerId);

        // Immediately switch all items to view mode when dragging starts
        enableGlobalReadOnly();
        setActiveItemId(null);

        // local state update
        setActiveId(active.id?.toString());
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        const overId = over?.id;

        consoleLog('[onDragOver]', ' active: ', active.id, ' over :', overId);

        // If the dragged item is over the trash container, is null/undefined, or represents a container sorting drag,
        // immediately clear any active task list container highlight.
        if (overId == null || overId === TRASH_ID || active.id in items) {
          setShowDroppableContainerId(null);
          return;
        }

        const overContainer = findContainer(overId.toString());
        const activeContainer = findContainer(active.id.toString());

        // If the pointer is over the right detailed panel or any non-container droppable zone,
        // overContainer will resolve to undefined, which correctly clears list container highlights.
        if (!overContainer || !activeContainer) {
          setShowDroppableContainerId(null);
          return;
        }

        setShowDroppableContainerId(overContainer);

        /*
        /// Original logic
        if (activeContainer !== overContainer) {
          updateItems((items: ItemsMap) => {
            const activeItems = items[activeContainer];
            const overItems = items[overContainer];
            const overIndex = overItems.indexOf(overId.toString());
            const activeIndex = activeItems.indexOf(active.id.toString());
  
            let newIndex: number;
  
            if (overId in items) {
              newIndex = overItems.length + 1;
            } else {
              const isBelowOverItem =
                over &&
                active.rect.current.translated &&
                active.rect.current.translated.top >
                over.rect.top + over.rect.height;
  
              const modifier = isBelowOverItem ? 1 : 0;
  
              newIndex =
                overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }
  
            recentlyMovedToNewContainer.current = true;
  
            return {
              ...items,
              [activeContainer]: items[activeContainer].filter(
                (item) => item !== active.id
              ),
              [overContainer]: [
                ...items[overContainer].slice(0, newIndex),
                items[activeContainer][activeIndex],
                ...items[overContainer].slice(
                  newIndex,
                  items[overContainer].length
                ),
              ],
            } as ItemsMap;
          });
        }
        */
      }}
      onDragEnd={({ active, over, delta }) => {
        // To immediately switch to view mode when dragging starts
        disableGlobalReadOnly();

        const overIdStr = over?.id?.toString();

        if (overIdStr === 'detailed-panel-drop-zone') {
          const taskId = active.id.toString();
          const item = useTaskItemStore.getState().items[taskId];
          if (item && !item.completed) {
            useTaskItemStore.getState().toggleComplete(taskId);
          }
          setActiveItemId(taskId);
          setActiveId(null);
          return;
        }

        // over.id is null when the user clicks on the item and did not move it at all
        // it's expected to edit the item
        if (over?.id == null && active.id !== null) {
          setActiveItemId(active.id.toString());
        }

        /// This branch is executed when the user drags an Container, not an Item
        /// For now we don't allow dragging containers
        // if (active.id in items && over?.id) {
        //   setContainers((containers) => {
        //     const activeIndex = containers.indexOf(active.id?.toString());
        //     const overIndex = containers.indexOf(over.id?.toString());

        //     return arrayMove(containers, activeIndex, overIndex);
        //   });
        // }

        const activeContainer = findContainer(active.id?.toString()) as uuid;

        if (!activeContainer) {
          setActiveId(null);
          return;
        }

        const overId = over?.id;

        if (overId == null) {
          setActiveId(null);
          return;
        }

        /// This branch is not needed, as we don't allow trash container
        // if (overId === TRASH_ID) {
        //   updateItems((items) => ({
        //     ...items,
        //     [activeContainer]: items[activeContainer].filter(
        //       (id) => id !== activeId
        //     ),
        //   }));
        //   setActiveId(null);
        //   return;
        // }

        /// This branch is executed when the user drags an item to the placeholder column
        // if (overId === PLACEHOLDER_ID) {
        //   const newContainerId = getNextContainerId();
        //   unstable_batchedUpdates(() => {
        //     setContainers((containers) => [...containers, newContainerId]);
        //     updateItems((items) => ({
        //       ...items,
        //       [activeContainer]: items[activeContainer].filter(
        //         (id) => id !== activeId,
        //       ),
        //       [newContainerId]: [active.id?.toString()],
        //     }));
        //     setActiveId(null);
        //   });
        //   return;
        // }

        const overContainer = findContainer(overId?.toString());

        if (overContainer) {
          console.log(overContainer);
          if (activeContainer === overContainer) {
            // consoleLog('sorting ends, container does not change')
            // sorting item in the same container
            const activeIndex = items[activeContainer].indexOf(
              active.id?.toString()
            );
            const overIndex = items[overContainer].indexOf(overId?.toString());

            if (activeIndex !== overIndex) {
              updateItems((items) => ({
                ...items,
                [overContainer]: arrayMove(
                  items[overContainer],
                  activeIndex,
                  overIndex
                ),
              }));
            }
          } else {
            // consoleLog('sorting ends, moving to the start/end of the other container')
            // now the item is dragged over a different container
            // no sorting, instead insert directly to its ~~top~~ bottom
            updateItems((items) => ({
              ...items,
              [activeContainer]: items[activeContainer].filter(
                (id) => id !== active?.id.toString()
              ),
              [overContainer]:
                overContainer in defaultSystemListMap
                  ? [active?.id.toString(), ...items[overContainer]]
                  : [...items[overContainer], active?.id.toString()],
            }));

            // scroll the content of the overContainer to the bottom

            const newPriority = getPriority(overContainer);
            // and update the keys, change `priority` to current level
            updateItemContent(
              active?.id.toString(),
              undefined,
              undefined,
              undefined,
              newPriority ? { priority: newPriority } : {}
            );
          }
        }

        // reset current sorting container
        setSortingContainerId(null);
        setShowDroppableContainerId(null);

        setActiveId(null);
      }}
      cancelDrop={cancelDrop}
      onDragCancel={onDragCancel}
      modifiers={modifiers}
    >
      <div
        className={cn(styles.container, {
          [styles.hasActiveItem]: !!activeItemId,
        })}
      >
        <div
          className={styles.primaryListPanel}
          style={
            {
              ['--sidebar-width' as any]: `${sidebarWidth}px`,
            } as React.CSSProperties
          }
        >
          <div
            ref={sidebarListsContainerRef}
            className={styles.sidebarListsContainer}
          >
            {orderedListIds.map((id) => renderDroppableContainer(id))}
          </div>
          <div className={styles.sidebarFooter}>
            <Flex gap="2" style={{ width: '100%' }}>
              <Button
                variant="soft"
                onClick={handleCreateNewList}
                className={styles.createListButton}
                style={{ flexGrow: 1 }}
              >
                <PlusIcon />
                <span>Create New List</span>
              </Button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <IconButton
                    variant="soft"
                    color="gray"
                    title="Menu"
                    style={{ cursor: 'pointer' }}
                  >
                    <HamburgerMenuIcon />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" style={{ minWidth: '250px' }}>
                  <DropdownMenu.Item
                    onClick={() => setIsSettingsOpen(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    <GearIcon />
                    <Text>Settings</Text>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => setIsTrashOpen(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TrashIcon />
                    <Text>Trash</Text>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={handleExportBackup}
                    style={{ cursor: 'pointer' }}
                  >
                    <DownloadIcon />
                    <Text>Export</Text>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Flex>
          </div>
          {/* Vertical Resize Handle for sidebar width */}
          <div
            className={styles.colResizer}
            onMouseDown={handleSidebarMouseDown}
          />
        </div>

        {/* 
          Keep DetailedPanel mounted if a task is selected OR (on desktop only) if a task is actively being dragged.
          This ensures the Dnd-kit useDroppable hook remains registered in the DOM during the entire drag session.
          On mobile, we totally disable this right-side drop zone to prevent the panel from taking over the screen.
        */}
        {(activeItemId || (!isMobile && activeId)) && (
          <div className={styles.detailedPanel}>
            <DetailedPanel
              onClose={() => setActiveItemId(null)}
              activeDragId={activeId}
            />
          </div>
        )}
      </div>
      {createPortal(
        <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
          {activeId
            ? // containers.includes(activeId)
            // ? renderContainerDragOverlay(activeId) :
            renderSortableItemDragOverlay(activeId)
            : null}
        </DragOverlay>,
        document.body
      )}
      {/* Settings Dialog */}
      <Dialog.Root open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Settings</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            DoneLit configuration and settings.
          </Dialog.Description>

          <Flex direction="column" gap="3" style={{ padding: '8px 0' }}>
            <Text size="2">
              Privacy-first local task manager. All your data stays locally on
              your device.
            </Text>
            <Text size="1" color="gray">
              Version: 1.0.0
            </Text>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" style={{ cursor: 'pointer' }}>
                Close
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Trash Dialog */}
      <Dialog.Root open={isTrashOpen} onOpenChange={setIsTrashOpen}>
        <Dialog.Content maxWidth="500px">
          <Dialog.Title>Trash</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            View or restore deleted tasks. Restoring a task will put it back to
            its original list.
          </Dialog.Description>

          {deletedItems.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="2"
              style={{ padding: '32px 0' }}
            >
              <span style={{ fontSize: '32px' }}>🗑️</span>
              <Text color="gray" size="2">
                Trash is empty
              </Text>
            </Flex>
          ) : (
            <ScrollArea scrollbars="vertical" style={{ maxHeight: '350px' }}>
              <Flex direction="column" gap="2">
                {deletedItems.map((item) => (
                  <Flex
                    key={item.id}
                    align="center"
                    justify="between"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor:
                        activeItemId === item.id ? '#f1f5f9' : '#ffffff',
                      transition: 'background-color 0.2s ease',
                    }}
                    onClick={() => {
                      setActiveItemId(item.id);
                      setIsTrashOpen(false);
                    }}
                  >
                    <Flex
                      direction="column"
                      gap="1"
                      style={{ flexGrow: 1, minWidth: 0, paddingRight: '8px' }}
                    >
                      <Text
                        size="2"
                        weight="bold"
                        style={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.title || 'Untitled Task'}
                      </Text>
                      <Text size="1" color="gray">
                        Deleted:{' '}
                        {item.deletedAt
                          ? new Date(item.deletedAt).toLocaleString()
                          : 'Unknown'}
                      </Text>
                    </Flex>
                    <Flex gap="2" style={{ flexShrink: 0 }}>
                      <Button
                        size="1"
                        color="green"
                        variant="soft"
                        onClick={(e) => {
                          e.stopPropagation();
                          useTaskItemStore.getState().restoreItem(item.id);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        Restore
                      </Button>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </ScrollArea>
          )}

          <Flex gap="3" mt="4" justify="between">
            {deletedItems.length > 0 ? (
              <Button
                color="red"
                variant="solid"
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you want to permanently delete all items in the Trash?'
                    )
                  ) {
                    useTaskItemStore.getState().emptyTrash();
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                Empty Trash
              </Button>
            ) : (
              <div />
            )}
            <Dialog.Close>
              <Button variant="soft" color="gray" style={{ cursor: 'pointer' }}>
                Close
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </DndContext>
  );

  /**
   * When the item is being dragged, we render a drag overlay
   * @param id
   */
  function renderSortableItemDragOverlay(id: uuid) {
    consoleLog('renderSortableItemDragOverlay', id, activeId, items);
    const item = useTaskItemStore.getState().items[id];
    const completed = item?.completed;
    const failed = item?.failed;
    const justUpdated = item?.justUpdated;
    const toggleComplete = useTaskItemStore.getState().toggleComplete;
    const toggleFail = useTaskItemStore.getState().toggleFail;
    return (
      <Item
        value={<TaskItem id={id} defaultValue={item?.title || DEFAULT_TASK_TITLE} />}
        handle={handle}
        completed={completed}
        onToggleComplete={() => toggleComplete(id)}
        failed={failed}
        onToggleFail={() => toggleFail(id)}
        justUpdated={justUpdated}
        // style={getItemStyles({
        //   containerId: findContainer(id) as uuid,
        //   overIndex: -1,
        //   index: getIndex(id),
        //   value: id,
        //   isSorting: true,
        //   isDragging: true,
        //   isDragOverlay: true,
        // })}
        wrapperStyle={wrapperStyle({ index: 0 })}
        renderItem={renderItem}
        dragOverlay
        style={
          {
            border: '1px dashed #ccc',
          } as React.CSSProperties
        }
      />
    );
  }

  /**
   * When the container is being dragged, we render a drag overlay
   * @param containerId
   */
  // function renderContainerDragOverlay(containerId: uuid) {
  //   return (
  //     <Container
  //       id={containerId}
  //       label={`Column ${containerId} Drag Overlay`}
  //       columns={columns}
  //       style={{
  //         height: '100%',
  //       }}
  //       shadow
  //       unstyled={false}
  //     >
  //       {items[containerId].map((item, index) => (
  //         <Item
  //           key={item}
  //           value={item}
  //           handle={handle}
  //           style={getItemStyles({
  //             containerId,
  //             overIndex: -1,
  //             index: getIndex(item),
  //             value: item,
  //             isDragging: false,
  //             isSorting: false,
  //             isDragOverlay: false,
  //           })}
  //           color={getColor(item)}
  //           wrapperStyle={wrapperStyle({ index })}
  //           renderItem={renderItem}
  //         />
  //       ))}
  //     </Container>
  //   );
  // }

  // function handleRemove(containerID: uuid) {
  //   setContainers((containers) =>
  //     containers.filter((id) => id !== containerID),
  //   );
  // }

  function handleAddNewItem(
    containerID: uuid,
    options?: Record<string, string | boolean>
  ) {
    consoleLog('handleAddNewItem', containerID, options);
    const newItemId = createUuid();

    const containerItemIds = items[containerID] || [];
    const taskItems = useTaskItemStore.getState().items;
    const existingTitles = containerItemIds
      .map((id) => taskItems[id]?.title)
      .filter(Boolean) as string[];

    // Calculate next default title
    const usedIndices = new Set<number>();
    const regex = /^new task\s+(\d+)$/i;
    for (const title of existingTitles) {
      const trimmed = title.trim();
      if (trimmed.toLowerCase() === 'new task') {
        usedIndices.add(0);
      } else {
        const match = trimmed.match(regex);
        if (match) {
          usedIndices.add(parseInt(match[1], 10));
        }
      }
    }

    let nextTitle = 'New Task';
    if (usedIndices.size > 0) {
      const minNum = Math.min(...usedIndices);
      let nextIndex = minNum;
      while (usedIndices.has(nextIndex)) {
        nextIndex++;
      }
      nextTitle = nextIndex === 0 ? 'New Task' : `New Task ${nextIndex}`;
    }

    // Initialize in the task item store immediately
    useTaskItemStore
      .getState()
      .updateContent(newItemId, '', nextTitle, containerID);

    updateItems((items) => ({
      ...items,
      [containerID]: [...items[containerID], newItemId],
    }));

    setActiveItemId(newItemId);

    // unstable_batchedUpdates(() => {
    //   updateItems((items) => ({
    //     ...items,
    //     [containerID]: [...items[containerID], newItemId],
    //   }));
    //   const initialContent = `# ${nextTitle}\n\nPlease add content`;
    //   updateItemContent(newItemId, initialContent, nextTitle, undefined, {
    //     priority: getPriority(containerID),
    //   });
    //   setActiveItemId(newItemId);
    //   consoleLog('added a new item id', newItemId);
    // });
  }

  // function handleAddColumn() {
  //   const newContainerId = getNextContainerId();

  //   unstable_batchedUpdates(() => {
  //     setContainers((containers) => [...containers, newContainerId]);
  //     updateItems((items) => ({
  //       ...items,
  //       [newContainerId]: [],
  //     }));
  //   });
  // }

  function getNextContainerId() {
    // const containerIds = Object.keys(items);
    // const lastContainerId = containerIds[containerIds.length - 1];

    // return String.fromCharCode(lastContainerId.charCodeAt(0) + 1);
    return createUuid();
  }
}

/// TODO: this color tag can be further used
export function getColor(priority: any) {
  switch (priority) {
    case PRIORITY.EM_IMP_URG:
      return '#a1c4fd';
    case PRIORITY.EM_URG:
      return '#fcb69f';
    case PRIORITY.EM_IMP:
      return '#c1dfc4';
    case PRIORITY.EM_LOW:
      return '#c4c5c7';
  }

  return undefined;
}

function Trash({ id }: { id: uuid }) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        left: '50%',
        marginLeft: -150,
        bottom: 20,
        width: 300,
        height: 60,
        borderRadius: 5,
        border: '1px solid',
        borderColor: isOver ? 'red' : '#DDD',
      }}
    >
      Drop here to delete
    </div>
  );
}

interface SortableItemProps {
  containerId: uuid;
  id: uuid;
  index: number;
  handle: boolean;
  disabled?: boolean;
  useDragOverlay?: boolean;
  color?: string;

  style(args: any): React.CSSProperties;

  getIndex(id: uuid): number;

  renderItem(): React.ReactElement;
  onRemove?(): void;

  wrapperStyle({ index }: { index: number }): React.CSSProperties;
}

function SortableItem({
  disabled,
  useDragOverlay,
  id,
  index,
  handle,
  renderItem,
  onRemove,
  style,
  containerId,
  getIndex,
  wrapperStyle,
  color,
}: SortableItemProps) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    listeners,
    isDragging,
    isSorting,
    over,
    overIndex,
    transform,
    transition,
  } = useSortable({
    resizeObserverConfig: {},
    id,
    disabled,
  });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;
  const activeItemId = useTaskItemStore((s) => s.activeItemId);
  const setActiveItemId = useTaskItemStore((s) => s.setActiveItemId);
  const completed = useTaskItemStore((s) => s.items[id]?.completed);
  const failed = useTaskItemStore((s) => s.items[id]?.failed);
  const justUpdated = useTaskItemStore((s) => s.items[id]?.justUpdated);
  const toggleComplete = useTaskItemStore((s) => s.toggleComplete);
  const toggleFail = useTaskItemStore((s) => s.toggleFail);

  const emptyContent = 'New Task';
  const value = useMemo(
    () => <TaskItem id={id} defaultValue={emptyContent} />,
    [id, emptyContent]
  );

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      value={value}
      selected={activeItemId === id}
      completed={completed}
      onToggleComplete={() => toggleComplete(id)}
      failed={failed}
      onToggleFail={() => toggleFail(id)}
      justUpdated={justUpdated}
      onClick={() => setActiveItemId(id)}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
      index={index}
      wrapperStyle={wrapperStyle({ index })}
      style={style({
        index,
        value: id,
        isDragging,
        isSorting,
        overIndex: over ? getIndex(`${over.id}`) : overIndex,
        containerId,
      })}
      color={color}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      renderItem={renderItem}
      onRemove={onRemove}
      dragOverlay={useDragOverlay}
    />
  );
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}
